import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Col, Input, InputNumber, Modal, Row, Select, Switch } from 'antd'
import {
  errorNotification,
  patchRequest,
  postRequest,
  successNotification,
  trackingLineUrl,
  trackingLinesUrl
} from '../../config'
import { buildLinePayload, errorSentence, normalizePrefixes, validateLine } from './utils'

// A new line tracks both kinds until somebody says otherwise — the server's own defaults, and the
// combination that refuses the fewest real bills of lading.
const BLANK = {
  name: '',
  scac: '',
  shipsgoCarrierCode: '',
  prefixes: [],
  referencePrefixes: [],
  tracksContainer: true,
  tracksReference: true,
  roro: false,
  active: true,
  position: 0
}

const FIELD_LABELS = {
  name: 'Name',
  scac: 'SCAC',
  shipsgoCarrierCode: 'ShipsGo carrier code',
  prefixes: 'Container prefixes',
  referencePrefixes: 'Reference prefixes',
  tracksContainer: 'Tracks container',
  tracksReference: 'Tracks BL / booking',
  roro: 'RoRo',
  active: 'Active',
  position: 'Position'
}

// A 400 `invalid_line` carries `errors: {field: [sentence]}`. Shown field by field, because the
// `detail` alone ("Some of those details can't be saved.") leaves the operator hunting.
const fieldErrors = response => {
  const errors = response?.error?.response?.data?.errors
  if (!errors) return []
  return Object.keys(errors).reduce((list, field) => {
    const messages = Array.isArray(errors[field]) ? errors[field] : [errors[field]]
    return list.concat(messages.map(message => `${FIELD_LABELS[field] || field}: ${message}`))
  }, [])
}

const Help = ({ children }) => <div style={{ opacity: 0.65 }}>{children}</div>
Help.propTypes = { children: PropTypes.node }

// Add or edit one shipping line. The codes are uppercased as they are typed rather than on save,
// so what is on screen is what will be sent to ShipsGo.
const LineFormModal = ({ visible, line, onClose, onDone }) => {
  const [form, setForm] = useState(BLANK)
  const [errors, setErrors] = useState([])
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!visible) return
    setForm(line ? { ...BLANK, ...line } : BLANK)
    setErrors([])
    setSaving(false)
  }, [visible, line])

  const change = patch => setForm(current => ({ ...current, ...patch }))

  const submit = async () => {
    const problems = validateLine(form)
    setErrors(problems)
    if (problems.length) return
    // One request per click: a second POST while the first is in flight creates the line twice.
    if (saving) return

    const payload = buildLinePayload(form)
    const response = line
      ? await patchRequest(trackingLineUrl(line.id), payload, 'line', setSaving)
      : await postRequest(trackingLinesUrl, payload, 'line', setSaving)

    if (response.success) {
      successNotification('Saved.')
      onDone()
      return
    }
    // `prefix_taken` already names the prefix and the line that has it, so the server's sentence is
    // the whole answer; `invalid_line` needs its per-field list.
    const sentence = errorSentence(response, 'That line could not be saved.')
    const listed = fieldErrors(response)
    setErrors(listed.length ? listed : [sentence])
    errorNotification(sentence)
  }

  return (
    <Modal
      visible={visible}
      title={line ? `Edit ${line.name}` : 'Add a line'}
      okText="Save"
      confirmLoading={saving}
      onCancel={onClose}
      onOk={submit}
      width={720}
    >
      {errors.length ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="This line cannot be saved yet"
          description={
            <ul style={{ marginBottom: 0, paddingLeft: 18 }}>
              {errors.map(message => (
                <li key={message}>{message}</li>
              ))}
            </ul>
          }
        />
      ) : null}

      {/* A catalogue row (spec: tracking-carrier-catalogue D2): saving is the one-way door. */}
      {line && line.enriched === false ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="This line came from ShipsGo's carrier list."
          description="Saving it makes it a curated line: the nightly sync will keep its code verified but will no longer rename or reorder it."
        />
      ) : null}

      <Row gutter={[12, 12]}>
        <Col xs={24} md={12}>
          <label>Name</label>
          <Input
            maxLength={80}
            value={form.name}
            onChange={e => change({ name: e.target.value })}
          />
        </Col>
        <Col xs={24} md={12}>
          <label>SCAC</label>
          <Input
            maxLength={4}
            value={form.scac}
            onChange={e => change({ scac: e.target.value.toUpperCase() })}
          />
        </Col>
        <Col xs={24} md={12}>
          <label>ShipsGo carrier code</label>
          <Input
            value={form.shipsgoCarrierCode}
            onChange={e => change({ shipsgoCarrierCode: e.target.value.toUpperCase() })}
          />
          <Help>Exactly as ShipsGo lists it.</Help>
        </Col>
        <Col xs={24} md={12}>
          <label style={{ display: 'block' }}>Position</label>
          <InputNumber
            min={0}
            precision={0}
            style={{ width: '100%' }}
            value={form.position}
            onChange={value => change({ position: value })}
          />
          <Help>Lower comes first in the app&apos;s picker.</Help>
        </Col>
        <Col xs={24} md={12}>
          <label style={{ display: 'block' }}>Container prefixes</label>
          <Select
            mode="tags"
            tokenSeparators={[',', ' ']}
            style={{ width: '100%' }}
            value={form.prefixes}
            onChange={values => change({ prefixes: normalizePrefixes(values) })}
          />
          <Help>
            The first four letters of this line&apos;s own containers, e.g. MSCU, MEDU. Leave
            leased-box prefixes out — they would suggest the wrong line.
          </Help>
        </Col>
        <Col xs={24} md={12}>
          <label style={{ display: 'block' }}>Reference prefixes</label>
          <Select
            mode="tags"
            tokenSeparators={[',', ' ']}
            style={{ width: '100%' }}
            value={form.referencePrefixes}
            onChange={values => change({ referencePrefixes: normalizePrefixes(values) })}
          />
          <Help>
            The SCAC letters that start this line&apos;s bills of lading or bookings, e.g. MEDU for
            MSC. Used only to suggest the line to the customer.
          </Help>
        </Col>
        <Col xs={24} md={6}>
          <label style={{ display: 'block' }}>Tracks container</label>
          <Switch
            checked={!!form.tracksContainer}
            onChange={value => change({ tracksContainer: value })}
          />
        </Col>
        <Col xs={24} md={6}>
          <label style={{ display: 'block' }}>Tracks BL / booking</label>
          <Switch
            checked={!!form.tracksReference}
            onChange={value => change({ tracksReference: value })}
          />
        </Col>
        <Col xs={24} md={6}>
          <label style={{ display: 'block' }}>RoRo</label>
          <Switch checked={!!form.roro} onChange={value => change({ roro: value })} />
          <Help>Cars driven on board. RoRo lines usually track by BL or booking only.</Help>
        </Col>
        <Col xs={24} md={6}>
          <label style={{ display: 'block' }}>Active</label>
          <Switch checked={form.active !== false} onChange={value => change({ active: value })} />
        </Col>
      </Row>
    </Modal>
  )
}

LineFormModal.propTypes = {
  visible: PropTypes.bool,
  line: PropTypes.object,
  onClose: PropTypes.func,
  onDone: PropTypes.func
}

export default LineFormModal
